import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CollaboratorsActivity, ManageCollaboratorsActivity } from '@melio/ap-activities';
import { ApprovalWorkflowsSettings } from '@melio/ap-activities';
import { SubscriptionPlansSettingsScreen } from '@melio/ap-activities';
import { ApprovalWorkflowSettingsErrorState } from '@melio/ap-activities/src/components/settings/ApprovalWorkflowsSettings/ApprovalWorkflowSettingsErrorState';
import { useBreakpoint } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { usePermissions } from '@melio/platform-permissions';

import { usePartnerConfig } from '@/hooks/partners';
import { useRouter } from '@/hooks/router.hooks';
import { useActiveScreen } from '@/hooks/useActiveScreen';
import { useHasAccessToFirm } from '@/hooks/useHasAccessToFirm.hooks';
import { SettingsScreen } from '@/screens/settings/Settings.screen';
import { ScreensEnum } from '@/store/app/app.types';
import { AccountSoftwareSettingsWidget } from '@/widgets/settings-page/AccountSoftwareSettingsPage/AccountSoftwareSettings.widget';
import { BillingSettings } from '@/widgets/settings-page/BillingSettingsPage/BillingSettings.widget';
import { CompanySettings } from '@/widgets/settings-page/CompanySettingPage/CompanySettings.widget';
import { NotificationPreferences } from '@/widgets/settings-page/NotificationPreferencesPage/NotificationPreferences.widget';
import { PaymentsMethodPage } from '@/widgets/settings-page/PaymentMethodsSettingsPage/PaymentMethodsSettings.widget';
import { ProfileSettings } from '@/widgets/settings-page/ProfileSettingsPage/ProfileSettings.widget';
import { ReceivingMethods } from '@/widgets/settings-page/ReceivingMethodsPage/ReceivingMethods.widget';
import { Support } from '@/widgets/settings-page/SupportPage/Support.widget';

export const SettingsRoute = () => {
  useActiveScreen(ScreensEnum.settings);
  const { generateSettingsProfileTabLink, goToCreateApprovalWorkflow, goToEditApprovalWorkflow } = useRouter();
  const { isExtraSmallScreen } = useBreakpoint();
  const hasAccessToFirm = useHasAccessToFirm();
  const [accountManagerAccessCollaboratorsEnabled] = useFeature<boolean>(
    FeatureFlags.AccountManagerAccessCollaborators,
    false,
  );
  const [notificationPreferencesEnabled] = useFeature<boolean>(FeatureFlags.NotificationPreferences, false);
  const [displayReceivingMethodSettingItem] = useFeature<boolean>(FeatureFlags.DisplayReceivingMethodsItem, false);
  const {
    partnerConfig: { allowEditFirstAndLastName },
  } = usePartnerConfig();

  const { can } = usePermissions();
  const hasCollaboratorPermissions =
    can({ subject: 'collaborator', action: 'read' }) || can({ subject: 'invitation', action: 'read' });
  const [isApprovalWorkflowsFeatureEnabled] = useFeature(FeatureFlags.ApprovalWorkflows, false);

  const isApprovalWorkflowsEnabled =
    isApprovalWorkflowsFeatureEnabled && can({ action: 'read', subject: 'approvalWorkflow' });

  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const [isPlatformUserManagementAdjustmentsFiservEnabled] = useFeature(
    FeatureFlags.PlatformUserManagementAdjustmentsFiserv,
    false,
  );

  return (
    <Routes>
      <Route path={'/'} element={<SettingsScreen />}>
        {!isExtraSmallScreen && <Route index element={<Navigate to={generateSettingsProfileTabLink()} />} />}
        <Route path={'/profile'} element={<ProfileSettings allowEdit={allowEditFirstAndLastName} />} />
        {hasAccessToFirm ? <Route path={'/company'} element={<CompanySettings />} /> : null}
        {notificationPreferencesEnabled && hasAccessToFirm && (
          <Route path={'/notification-preferences'} element={<NotificationPreferences />} />
        )}
        {isApprovalWorkflowsEnabled && hasAccessToFirm && (
          <Route
            path={'/approval-workflows'}
            element={
              <ErrorBoundary fallback={<ApprovalWorkflowSettingsErrorState />}>
                <ApprovalWorkflowsSettings
                  onAction={(workflow) => {
                    if (workflow.action === 'add') {
                      goToCreateApprovalWorkflow();
                    } else if (workflow.action === 'edit') {
                      goToEditApprovalWorkflow(workflow.meta.id);
                    }
                  }}
                />
              </ErrorBoundary>
            }
          />
        )}
        {accountManagerAccessCollaboratorsEnabled && hasCollaboratorPermissions && hasAccessToFirm && (
          <Route
            path={'/collaborators/*'}
            element={
              isPlatformUserManagementAdjustmentsFiservEnabled ? (
                <CollaboratorsActivity />
              ) : (
                <ManageCollaboratorsActivity />
              )
            }
          />
        )}
        {hasAccessToFirm && <Route path={'/accounting-software'} element={<AccountSoftwareSettingsWidget />} />}
        {hasAccessToFirm && <Route path={'/payment-methods'} element={<PaymentsMethodPage />} />}
        {hasAccessToFirm && (
          <Route path={'/payment-methods/:fundingSourceId/verify'} element={<PaymentsMethodPage />} />
        )}
        {hasAccessToFirm && <Route path={'/billing'} element={<BillingSettings />} />}
        {isPlatformSubscriptionsEnabled && hasAccessToFirm && (
          <Route path={'/subscription-plans'} element={<SubscriptionPlansSettingsScreen />} />
        )}
        <Route path={'/support'} element={<Support />} />
        {displayReceivingMethodSettingItem && hasAccessToFirm ? (
          <Route path={'/receiving-methods'} element={<ReceivingMethods />} />
        ) : null}
      </Route>
    </Routes>
  );
};
